import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import {
  NgOptionTemplateDirective,
  NgSelectComponent,
  NgSelectModule,
} from '@ng-select/ng-select';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import {
  formatDisplayText,
  formatValueSafely,
} from '@shared-lib/utils/text-case.utils';

@Component({
  selector: 'app-global-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatPseudoCheckboxModule,
    NgSelectComponent,
    NgOptionTemplateDirective,
    NgSelectModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GlobalDropdownComponent),
      multi: true,
    },
  ],
  templateUrl: './global-dropdown.component.html',
  styleUrl: './global-dropdown.component.scss',
})
export class GlobalDropdownComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() data: any;
  @Input() options: any[] = [];
  @Output() filterChanged = new EventEmitter<any>();

  selected: any = [];
  searchCtrl = new FormControl('');
  filteredOptions: any[] = [];
  isDropdownOpen = false;

  private suppressSelectionChange = false;
  private destroy$ = new Subject<void>();
  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnInit(): void {
    this.filteredOptions = [...(this.options || [])];

    if (!!this.data?.apiUrl) {
      this.configService
        .getConfig(this.data.apiUrl, this.data, undefined, this.data.method)
        .subscribe({
          next: (res) => {
            this.options = res?.data || [];
            this.filteredOptions = [...this.options];
          },
          error: (err) => console.error(err),
        });
    }

    this.searchCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchText: string | null) => {
        const term = String(searchText || '')
          .trim()
          .toLowerCase();
        this.filteredOptions = !term
          ? [...this.options]
          : this.options.filter((opt) =>
              String(opt || '')
                .toLowerCase()
                .includes(term),
            );
      });
  }

  get isAllSelected(): boolean {
    return (
      this.options?.length > 0 &&
      Array.isArray(this.selected) &&
      this.selected.length === this.options.length
    );
  }

  get placeholderText(): string {
    return (
      this.data?.placeholder ||
      (this.data?.disabled
        ? this.data?.disabledPlaceholder || 'Select a value first'
        : 'Select')
    );
  }

  get showSelectAll(): boolean {
    return this.data?.isMultiSelect && this.data?.showSelectAll !== false;
  }

  get showSearch(): boolean {
    return this.data?.showSearch !== false;
  }

  get searchPlaceholder(): string {
    return this.data?.searchPlaceholder || 'Search';
  }

  isItemSelected(item: any): boolean {
    return Array.isArray(this.selected)
      ? this.selected.includes(item)
      : this.selected === item;
  }

  toggleSelectAll(event: any): void {
    if (!this.showSelectAll || this.data?.disabled) {
      return;
    }

    const isChecked = event?.target?.checked;
    this.suppressSelectionChange = true;
    this.selected = isChecked ? [...this.options] : [];
    this.emitSelection();
    queueMicrotask(() => {
      this.suppressSelectionChange = false;
    });
  }

  onChange(): void {
    if (this.data?.disabled || this.suppressSelectionChange) {
      return;
    }

    this.emitSelection();
  }

  removeSelected(item: any): void {
    this.selected = Array.isArray(this.selected)
      ? this.selected.filter((selectedItem) => selectedItem !== item)
      : null;
    this.emitSelection();
  }

  onDropdownClose(): void {
    this.isDropdownOpen = false;
    this.searchCtrl.setValue('', { emitEvent: true });
  }

  onDropdownOpen(): void {
    this.isDropdownOpen = true;
  }

  writeValue(value: any): void {
    if (this.data?.isMultiSelect) {
      this.selected = Array.isArray(value) ? value : value ? [value] : [];
      return;
    }

    if (Array.isArray(value)) {
      this.selected = value[0] ?? null;
      return;
    }

    this.selected = value ?? null;
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  formatLabel(value: unknown): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatDisplayText(value, mode);
  }

  formatOption(value: unknown): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatValueSafely(value, mode);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private emitSelection(): void {
    this.onChangeFn(this.data?.isMultiSelect ? this.selected : this.selected ?? null);
    this.onTouchedFn();

    if (this.data?.key) {
      this.globalFilterService.updateFilterSelection(
        this.data.key,
        this.data?.isMultiSelect ? this.selected : this.selected ?? null,
        this.data?.isQueryParam,
        undefined,
        true,
        this.data?.filterListener || null,
      );
    }

    this.filterChanged.emit(this.data?.isMultiSelect ? this.selected : this.selected ?? null);
  }
}
